import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, type ButtonProps } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle
} from 'src/components/ui/dialog';
import { Typography } from 'src/components/ui/typography';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { ReactComponent as InfoCircleIcon } from 'src/assets/tailwind/info-circle.svg';
import { Icon } from 'src/components/ui/icon';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'destructive';
  cancelButtonProps?: ButtonProps;
  confirmButtonProps?: ButtonProps;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconColorClass?: string;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  variant = 'destructive',
  icon = InfoCircleIcon,
  iconColorClass = 'tw:text-icon-error',
  cancelButtonProps,
  confirmButtonProps
}) => {
  const { t } = useTranslation();
  const handleInteractOutside = (e: Event) => {
    e.preventDefault();
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideDefaultClose={true}
        onPointerDownOutside={handleInteractOutside}
        onEscapeKeyDown={handleInteractOutside}
        className="tw:max-w-[520px]"
      >
        <div className="tw:py-4xl tw:px-2xl tw:flex tw:flex-col tw:items-center tw:text-center tw:gap-s">
          <Icon
            icon={icon}
            colorClass={iconColorClass}
            sizeClass="tw:h-2xl tw:w-2xl"
          />
          <DialogTitle>
            <Typography variant="h3" colorClass="tw:text-text-primary">
              {title ?? t(T.areYouSure)}
            </Typography>
          </DialogTitle>
          <Typography variant="b1" colorClass="tw:text-text-secondary">
            {description ?? t(T.actionCannotBeUndone)}
          </Typography>
        </div>

        <DialogFooter className="tw:w-full">
          <div className="tw:flex tw:justify-between tw:items-center tw:w-full">
            <Button
              hierarchy={variant}
              size="small"
              text={cancelText ?? t(T.cancel)}
              onClick={onClose}
              {...cancelButtonProps}
            />
            <Button
              hierarchy="primary"
              size="small"
              text={confirmText ?? t(T.confirm)}
              onClick={onConfirm}
              {...confirmButtonProps}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
